import { faFilePdf, faFileSpreadsheet } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IonButton, IonCard, IonCol, IonRow } from '@ionic/react';
import { ExcelExport } from '@progress/kendo-react-excel-export';
import { GridPDFExport } from '@progress/kendo-react-pdf';
import { Breadcrumb, Breadcrumbs } from 'src/components/shared/Breadcrumbs';
import { useApi } from 'src/hooks/useApi';
import { useUtils } from 'src/hooks/useUtils';
import { Supplier } from 'src/interfaces/Supplier';
import { Page } from 'src/layouts/Page';
import { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { AppUserSelf } from 'src/interfaces/AppUserSelf';
import { SalesExcelExport } from './components/SalesExcelExport';
import { SalesPDFExport } from './components/SalesPDFExport';
import { SalesTable } from './components/SalesTable';

export interface SupplierSaleItem {
  account_number: number;
  account_name: string;
  item_number: number;
  item_ndc: string;
  item_uom: number | string;
  item_description: string | null;
  purchase_order_number: number | string;
  purchase_order_date: string;
  invoice_date: string | null;
  invoice_quantity: number;
  item_unit_price: number | string;
  item_total_price: number | string;
  invoice_number: number | string;
}

export const SalesPage: React.FC = () => {
  const api = useApi();
  const utils = useUtils();
  const [sales, setSales] = useState<SupplierSaleItem[]>([]);
  const supplier: Supplier | null = useSelector(
    (state: any) => state.supplier.supplier
  );
  const authUser: AppUserSelf | null = useSelector(
    (state: any) => state.auth.user
  );

  const breadcrumbs: Array<Breadcrumb> = [
    { label: 'Home', path: '/', direction: 'back' },
    { label: 'Sales' },
  ];

  const getSales = (queryParams?: any) => {
    if (!authUser) return
    api.get(`supplier/${authUser.organization_id}/supplier_sales`, queryParams).then((response) => setSales(response.data.result))
  };

  let _pdfExport: GridPDFExport | null = null;
  let _excelExport: ExcelExport | null = null;

  const formattedSales = useMemo(() => {
    return (sales || []).map(item => ({
      ...item,
      item_unit_price: utils.toUsd(Number(item.item_unit_price), 2),
      item_total_price: utils.toUsd(Number(item.item_total_price), 2)
    }));
  }, [sales, utils]);

  const exportExcel = () => {
    _excelExport?.save();
  };

  const exportPdf = () => {
    _pdfExport?.save();
  };

  return (
    <Page title="Supplier Sales">
      <Breadcrumbs breadcrumbs={breadcrumbs}>
        <IonButton onClick={exportExcel} size="small">
          Export to Excel
          <span slot="end">
            <FontAwesomeIcon size='5x' icon={faFileSpreadsheet} />
          </span>
        </IonButton>
        <IonButton onClick={exportPdf} size="small">
          Export to PDF
          <span slot="end">
            <FontAwesomeIcon icon={faFilePdf} />
          </span>
        </IonButton>
      </Breadcrumbs>
      <IonRow>
        <IonCol>
          <IonCard>
            <SalesExcelExport
              data={formattedSales}
              supplierName={supplier?.name || ''}
              onRef={(exporter) => { _excelExport = exporter; }}
            />
            <SalesPDFExport
              data={formattedSales}
              supplierName={supplier?.name || ''}
              onRef={(exporter) => { _pdfExport = exporter; }}
            />
          </IonCard>
        </IonCol>
      </IonRow>
      <IonRow>
        <IonCol size="12">
          <IonCard className="ion-no-padding">
            <SalesTable setDownloadSales={(queryParams) => getSales(queryParams)} />
          </IonCard>
        </IonCol>
      </IonRow>
    </Page>
  );
};
